import { Pause, PlayOne } from '@icon-park/react';
import { noop } from 'lodash';
import { createContext, memo, useCallback, useRef } from 'react';

import { Button } from '@didaskill/ui/design-system';
import { useBoolState, useMemoizedBundle } from '@didaskill/ui/utils';

export type AudioResourceItemContextValue = {
  isAudioPlaying: boolean;
  onAudioPlaying: () => void;
  onAudioPaused: () => void;
  onAudioTogglePlayback: () => void;
};

export const AudioResourceItemContext = createContext<AudioResourceItemContextValue>({
  isAudioPlaying: false,
  onAudioPlaying: noop,
  onAudioPaused: noop,
  onAudioTogglePlayback: noop,
});

export type AudioResourceItemContextProviderProps = {
  audioHref: string;
};

export const AudioResourceItemContextProviderBase = ({
  audioHref,
}: AudioResourceItemContextProviderProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  const [isAudioPlaying, onAudioPlaying, onAudioPaused] = useBoolState(false);

  const onAudioTogglePlayback = useCallback(() => {
    if (isAudioPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
  }, [isAudioPlaying]);

  const contextValue = useMemoizedBundle({
    isAudioPlaying,
    onAudioPlaying,
    onAudioPaused,
    onAudioTogglePlayback,
  });

  return (
    <AudioResourceItemContext.Provider value={contextValue}>
      {/* Can't generate a caption track... */}
      {/* biome-ignore lint/a11y/useMediaCaption: no caption track can be generated for this audio */}
      <audio hidden ref={audioRef} onPlay={onAudioPlaying} onPause={onAudioPaused}>
        <source src={audioHref} type="audio/mpeg" />
      </audio>

      <Button
        size={Button.SIZES.LARGE}
        outline
        icon={isAudioPlaying ? <Pause /> : <PlayOne />}
        onClick={onAudioTogglePlayback}
      />
    </AudioResourceItemContext.Provider>
  );
};

AudioResourceItemContextProviderBase.displayName = 'AudioResourceItemContextProvider';

export default memo(AudioResourceItemContextProviderBase);
